import React, { useState } from 'react';

/**
 * ROIBenchmarkCard
 * Displays grounded ROI data from the benchmark database.
 * Unlike generic "potential savings" copy, these figures cite real sources + sample sizes.
 */
export default function ROIBenchmarkCard({ roiProjection, roiBenchmarks = [] }) {
    const [expanded, setExpanded] = useState(false);

    // Use the best benchmark if available, fall back to blueprint ROI projection
    const primary = roiBenchmarks.length > 0 ? roiBenchmarks[0] : null;

    const hoursPerWeek = primary?.hours_saved_per_week || roiProjection?.hours_saved_per_week || '6–10';
    const costPerMonth = primary?.cost_recovered_per_month || roiProjection?.cost_recovered_per_month || '$1,500–$3,000';
    const payback = primary?.payback_period || roiProjection?.payback_period || '3–4 weeks';
    const toolCost = roiProjection?.monthly_tool_cost || '~$150–$350';
    const keyWin = primary?.key_win || (roiProjection?.key_wins || [])[0] || '';
    const source = primary?.source || '';
    const sampleSize = primary?.sample_size || 0;

    return (
        <div style={{
            background: 'rgba(15, 20, 40, 0.6)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
        }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{
                    width: '40px', height: '40px', borderRadius: '10px',
                    background: 'rgba(16, 185, 129, 0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '20px',
                }}>
                    💰
                </div>
                <div>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#f1f5f9' }}>
                        ROI Projection
                    </h3>
                    <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>
                        {primary
                            ? `Based on ${sampleSize > 0 ? `${sampleSize.toLocaleString()} real businesses` : 'industry benchmarks'}`
                            : 'Industry benchmark data not available yet — using general SMB estimates.'}
                    </p>
                </div>
                {primary && (
                    <span style={{
                        marginLeft: 'auto',
                        background: 'rgba(16, 185, 129, 0.1)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        color: '#10b981',
                        fontSize: '10px',
                        fontWeight: '700',
                        padding: '3px 10px',
                        borderRadius: '100px',
                    }}>
                        REAL DATA
                    </span>
                )}
            </div>

            {/* Key metrics grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                gap: '12px',
                marginBottom: '16px',
            }}>
                {[
                    { label: 'Hours Saved / Week', value: hoursPerWeek, icon: '⏱️' },
                    { label: 'Value Recovered / Month', value: costPerMonth, icon: '📈' },
                    { label: 'Payback Period', value: payback, icon: '🔄' },
                    { label: 'Monthly Tool Cost', value: toolCost, icon: '🛠️' },
                ].map(({ label, value, icon }) => (
                    <div key={label} style={{
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.06)',
                        borderRadius: '10px',
                        padding: '14px',
                        textAlign: 'center',
                    }}>
                        <div style={{ fontSize: '20px', marginBottom: '6px' }}>{icon}</div>
                        <div style={{
                            fontSize: '15px', fontWeight: '800', color: '#10b981',
                            marginBottom: '4px', lineHeight: 1,
                        }}>
                            {value}
                        </div>
                        <div style={{ fontSize: '10px', color: '#64748b', lineHeight: '1.3' }}>{label}</div>
                    </div>
                ))}
            </div>

            {/* Key win highlight */}
            {keyWin && (
                <div style={{
                    padding: '12px 16px',
                    background: 'rgba(16, 185, 129, 0.06)',
                    border: '1px solid rgba(16, 185, 129, 0.15)',
                    borderRadius: '10px',
                    fontSize: '13px',
                    color: '#34d399',
                    marginBottom: source ? '12px' : 0,
                }}>
                    ✨ <strong>Key Win:</strong> {keyWin}
                </div>
            )}

            {/* Source attribution */}
            {source && (
                <div style={{
                    fontSize: '11px', color: '#475569',
                    fontStyle: 'italic',
                    textAlign: 'right',
                }}>
                    Source: {source}{sampleSize > 0 ? ` (n=${sampleSize.toLocaleString()})` : ''}
                </div>
            )}

            {/* More benchmarks */}
            {roiBenchmarks.length > 1 && (
                <div style={{ marginTop: '12px' }}>
                    <button
                        onClick={() => setExpanded(!expanded)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#6366f1',
                            fontSize: '12px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            padding: 0,
                        }}
                    >
                        {expanded ? '▲ Hide' : '▼ Show'} {roiBenchmarks.length - 1} more benchmarks
                    </button>
                    {expanded && (
                        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {roiBenchmarks.slice(1).map((b, idx) => (
                                <div key={idx} style={{
                                    padding: '10px 14px',
                                    background: 'rgba(255,255,255,0.02)',
                                    border: '1px solid rgba(255,255,255,0.06)',
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                    color: '#94a3b8',
                                }}>
                                    <strong style={{ color: '#f1f5f9' }}>{b.tool}:</strong>{' '}
                                    saves {b.hours_saved_per_week} hrs/week, recovers {b.cost_recovered_per_month}/mo.{' '}
                                    <span style={{ color: '#64748b', fontStyle: 'italic' }}>
                                        {b.key_win}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Blueprint key wins */}
            {!primary && roiProjection?.key_wins?.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                    <div style={{
                        fontSize: '11px', fontWeight: '700', color: '#64748b',
                        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px',
                    }}>
                        Expected Outcomes
                    </div>
                    {roiProjection.key_wins.map((win, idx) => (
                        <div key={idx} style={{
                            display: 'flex', gap: '6px', marginBottom: '4px',
                            fontSize: '12px', color: '#94a3b8',
                        }}>
                            <span style={{ color: '#10b981' }}>✓</span>
                            <span>{win}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
